import React, { useId, useLayoutEffect, useRef } from "react"
import { Button } from "../button"
import { Icon } from "../icon"
import { useTrackScrollOverflow } from "../use-track-scroll-overflow"
import { VisuallyHidden } from "../visually-hidden"
import { allowsMotion, cn } from "../utils"
import { TabLinksContext } from "./tab-links-context"
import { TabLinksItem } from "./tab-links-item"

export type { TabLinksItemProps } from "./tab-links-item"

// =============================================================================
// Props
// =============================================================================

export type TabLinksProps = {
  /**
   * The accessible name for the navigation landmark. Differentiates it from
   * other `<nav>`s, such as the main `Navigation` (e.g. "Broadcast sections").
   */
  accessibleName: string
  children?: React.ReactNode
  className?: string
  /**
   * The consumer's current path (e.g. `window.location.pathname`). An item is
   * active when its `href` matches this exactly.
   */
  currentPath: string
}

// =============================================================================
// Styles
// =============================================================================

const tabLinksStyles = [
  "relative",
  // The gutter fits the item focus indicator's offset and width, so the
  // scroll container doesn't clip it
  "[--tab-links-focus-indicator-offset:0.5rem]",
  "[--tab-links-gutter:calc(var(--tab-links-focus-indicator-offset)+0.125rem)]",
  "mx-[calc(var(--tab-links-gutter)*-1)]"
]

const tabLinksNavStyles = [
  "flex gap-x-6",
  "overflow-x-auto overscroll-x-contain",
  "px-(--tab-links-gutter)",
  "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
  // The fade is fully transparent under the scroll button, so the button reads
  // on any background, and wider than the gap between items, so it always
  // covers some of a hidden item
  "[--tab-links-scroll-button-size:1.5rem] [--tab-links-fade-size:3.5rem]",
  "[--tab-links-fade-left:black] [--tab-links-fade-right:black]",
  // Prevent focus indicators from being clipped by fades on scroll
  "scroll-px-[calc(var(--tab-links-fade-size)+var(--tab-links-gutter))]",
  "data-[overflow-left]:[--tab-links-fade-left:transparent] data-[overflow-right]:[--tab-links-fade-right:transparent]",
  "[mask-image:linear-gradient(to_right,var(--tab-links-fade-left),var(--tab-links-fade-left)_var(--tab-links-scroll-button-size),black_var(--tab-links-fade-size),black_calc(100%_-_var(--tab-links-fade-size)),var(--tab-links-fade-right)_calc(100%_-_var(--tab-links-scroll-button-size)),var(--tab-links-fade-right))]"
]

const tabLinksScrollButtonContainerStyles =
  "absolute inset-y-0 flex items-center"

// =============================================================================
// Helpers
// =============================================================================

type ScrollDirection = "start" | "end"

function getScrollPadding(nav: HTMLElement) {
  const { scrollPaddingLeft, scrollPaddingRight } = getComputedStyle(nav)
  return {
    left: parseFloat(scrollPaddingLeft) || 0,
    right: parseFloat(scrollPaddingRight) || 0
  }
}

// Scrolls only the nav, unlike `scrollIntoView`, which would also scroll the
// page to the nav on load
function scrollItemClearOfFades(nav: HTMLElement, item: Element) {
  const scrollPadding = getScrollPadding(nav)
  const navRect = nav.getBoundingClientRect()
  const itemRect = item.getBoundingClientRect()
  const hiddenLeft = navRect.left + scrollPadding.left - itemRect.left
  const hiddenRight = itemRect.right - (navRect.right - scrollPadding.right)

  if (hiddenLeft > 0) nav.scrollLeft -= hiddenLeft
  else if (hiddenRight > 0) nav.scrollLeft += hiddenRight
}

// Scrolls by the width between the fades, so the item cut off by one fade
// becomes the first fully visible item. Narrow containers, where the fades
// leave little room, still scroll by half their width.
function scrollByPage(nav: HTMLElement, direction: ScrollDirection) {
  const scrollPadding = getScrollPadding(nav)
  const widthBetweenFades =
    nav.clientWidth - scrollPadding.left - scrollPadding.right
  nav.scrollBy({
    left:
      (direction === "start" ? -1 : 1) *
      Math.max(widthBetweenFades, nav.clientWidth / 2),
    behavior: allowsMotion() ? "smooth" : "auto"
  })
}

// =============================================================================
// Component
// =============================================================================

/**
 * Tab links for cross-page navigation. Renders a `<nav>` landmark containing
 * `<a>` elements that *look* like tabs but move the user between pages (each
 * link has its own URL). The active link is derived from `currentPath`.
 *
 * The links scroll horizontally when they overflow and fade at the edges, where
 * a button scrolls them by a page for pointer users. Keyboard users reach every
 * link with Tab, so the buttons are hidden from assistive tech and the tab
 * order. The active link and a keyboard-focused link are scrolled clear of the
 * fades.
 *
 * Compose with `TabLinks.Item` for each link.
 *
 * For in-page panel switching (same URL, switching content), use a tabs
 * component instead, which renders a real `role="tablist"` with arrow-key
 * navigation.
 */
export function TabLinksRoot({
  accessibleName,
  children,
  className,
  currentPath
}: TabLinksProps) {
  const accessibleNameId = useId()
  const ref = useRef<HTMLElement>(null)
  const { hasLeftOverflow, hasRightOverflow } = useTrackScrollOverflow(ref)

  useLayoutEffect(() => {
    const nav = ref.current
    const activeItem = nav?.querySelector('[aria-current="page"]')
    if (nav && activeItem) scrollItemClearOfFades(nav, activeItem)
  }, [currentPath])

  // The browser's own scroll on focus doesn't reliably honor `scroll-padding`,
  // so the focused item is scrolled into view again explicitly.
  const scrollFocusedItemIntoView = (event: React.FocusEvent<HTMLElement>) => {
    if (event.target === event.currentTarget) return
    if (!event.target.matches(":focus-visible")) return
    event.target.scrollIntoView({ block: "nearest", inline: "nearest" })
  }

  const renderScrollButton = (direction: ScrollDirection) => (
    <div
      className={cn(
        tabLinksScrollButtonContainerStyles,
        direction === "start" ? "left-0" : "right-0"
      )}
    >
      <Button
        aria-hidden
        tabIndex={-1}
        variant="tertiary"
        size="small"
        telemetry={false}
        data-slot="tab-links-scroll-button"
        // Keep focus where it was rather than on a button hidden from
        // assistive tech, which unmounts once there's nothing left to scroll
        onMouseDown={event => event.preventDefault()}
        onClick={() => ref.current && scrollByPage(ref.current, direction)}
      >
        <Icon name={direction === "start" ? "left" : "right"} size="xs" />
      </Button>
    </div>
  )

  return (
    <TabLinksContext.Provider value={{ currentPath }}>
      <div className={cn(tabLinksStyles, className)} data-slot="tab-links">
        <nav
          ref={ref}
          className={cn(tabLinksNavStyles)}
          aria-labelledby={accessibleNameId}
          data-overflow-left={hasLeftOverflow ? "" : undefined}
          data-overflow-right={hasRightOverflow ? "" : undefined}
          data-slot="tab-links-nav"
          onFocus={scrollFocusedItemIntoView}
        >
          <VisuallyHidden id={accessibleNameId}>
            {accessibleName}
          </VisuallyHidden>
          {children}
        </nav>
        {hasLeftOverflow && renderScrollButton("start")}
        {hasRightOverflow && renderScrollButton("end")}
      </div>
    </TabLinksContext.Provider>
  )
}

// =============================================================================
// Compound component
// =============================================================================

interface TabLinksComponent {
  (props: TabLinksProps): React.JSX.Element
  Item: typeof TabLinksItem
}

export const TabLinks: TabLinksComponent = Object.assign(TabLinksRoot, {
  Item: TabLinksItem
} as const)
